import { useMemo, type ReactNode } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { PackageIcon, PlusIcon, XIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { SearchInput } from '@ems/ui/patterns/search-input'

import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatDateTime, formatMoney } from '@ems/ui/lib/format'

import { FilteredEmpty } from '#admin/components/list-filter.tsx'
import { OptionSelect } from '#admin/components/option-select.tsx'
import { pageSlice, TablePagination, usePagination } from '#admin/components/table-pagination.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { ServiceOrdersSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import { serviceOrdersQuery, servicePolicyQuery } from '#admin/features/exhibitor/queries.ts'
import {
  matchesServiceOrder,
  parseServiceOrderSearch,
} from '#admin/features/exhibitor/service-order-filter.ts'
import {
  byActionNeeded,
  SERVICE_ORDER_STATUSES,
  onsiteDateText,
  PLACEMENT_LABEL,
  serviceOrderTone,
  type ServiceOrder,
} from '#admin/features/exhibitor/service-orders-api.ts'
import { label } from '#admin/features/registrations/api.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { useLiveSearch } from '#admin/lib/use-live-search.ts'

/**
 * `/exhibition/service-orders` — the service orders of the edition, as a worklist. An operator
 * takes a new order here for an exhibitor. The terms are on `/exhibition/service-terms`.
 *
 * Sorted by whose move it is: a submitted order waits for an operator to invoice it. The table is
 * not sortable, for the same reason as the exhibitor worklist. The search and the status filter
 * live in the URL, so the dashboard links to the submitted orders.
 */
export const Route = createFileRoute('/_authenticated/exhibition/service-orders/')({
  pendingComponent: ServiceOrdersSkeleton,
  validateSearch: parseServiceOrderSearch,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(serviceOrdersQuery()),
      context.queryClient.ensureQueryData(servicePolicyQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ]),
  component: ServiceOrdersPage,
})

/** `all` rather than `''`: Radix reserves the empty string for "nothing chosen". */
const ANY = 'all'

function ServiceOrdersPage() {
  const { data } = useSuspenseQuery(serviceOrdersQuery())
  const { data: policy } = useSuspenseQuery(servicePolicyQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const orders = data.items.toSorted(byActionNeeded)
  const waiting = orders.filter((order) => order.status === 'submitted').length

  // Filtered in the browser: the list is already here in full. The order stays `byActionNeeded`.
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [draft, setDraft] = useLiveSearch(
    search.q ?? '',
    (term) =>
      void navigate({
        search: (prev) => parseServiceOrderSearch({ ...prev, q: term }),
        replace: true,
      }),
  )
  const shown = orders.filter((order) => matchesServiceOrder(order, search))
  const filtered = search.q !== undefined || search.status !== undefined
  // One page at a time. A new search or status starts again at the first page.
  const { page, setPage, pageSize, setPageSize } = usePagination(
    `${search.status ?? ''}|${search.q ?? ''}`,
  )
  const paged = pageSlice(shown, page, pageSize)
  const clear = () => {
    setDraft('')
    void navigate({ search: {} })
  }

  const columns = useMemo<ColumnDef<ServiceOrder, never>[]>(
    () => [
      {
        accessorKey: 'companyName',
        header: 'Exhibitor',
        meta: { primary: true },
        cell: ({ row }) => (
          <OrderLink order={row.original}>
            {row.original.companyName}
            <span className="text-muted-foreground block font-mono text-xs">
              {row.original.applicationCode ?? 'Draft application'}
            </span>
          </OrderLink>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={serviceOrderTone(row.original.status)}>
            {label(row.original.status)}
          </StatusBadge>
        ),
      },
      {
        id: 'placement',
        header: 'Placement',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{PLACEMENT_LABEL[row.original.placement]}</span>
        ),
      },
      {
        id: 'submitted',
        header: 'Submitted',
        meta: { hideOnCard: true },
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.submittedAt === null ? '—' : formatDateTime(row.original.submittedAt)}
          </span>
        ),
      },
      {
        id: 'total',
        header: 'Total',
        meta: { align: 'end' },
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatMoney(row.original.total, row.original.currency)}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Service orders"
        description={
          <>
            {waiting === 0
              ? 'No order is waiting for an invoice.'
              : `${waiting} ${waiting === 1 ? 'order is' : 'orders are'} waiting for an invoice.`}{' '}
            {policy.onsiteFrom === null ? (
              <Link to="/exhibition/service-terms" className="underline underline-offset-2">
                Set when on-site prices start.
              </Link>
            ) : (
              <>
                On-site prices start{' '}
                {onsiteDateText(policy.onsiteFrom, manifest?.tenant.timezone ?? 'UTC')}.
              </>
            )}
          </>
        }
        actions={
          can(session, 'exhibitor.manage') ? (
            <Button asChild>
              <Link to="/exhibition/service-orders/new">
                <PlusIcon />
                New order
              </Link>
            </Button>
          ) : null
        }
      />

      {orders.length === 0 ? null : (
        <div className="flex flex-wrap items-end gap-2">
          <div role="search" className="flex w-full min-w-0 sm:w-auto sm:min-w-64 sm:flex-1">
            <SearchInput
              aria-label="Search by company, reference or order id"
              placeholder="Search company, reference or order id"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          </div>
          <OptionSelect
            aria-label="Status"
            className="w-full sm:w-44"
            value={search.status ?? ANY}
            onValueChange={(value) =>
              void navigate({
                search: (prev) => parseServiceOrderSearch({ ...prev, status: value }),
              })
            }
            options={[
              { value: ANY, label: 'Any status' },
              ...SERVICE_ORDER_STATUSES.map((value) => ({
                value,
                label: `${label(value)} · ${orders.filter((order) => order.status === value).length}`,
              })),
            ]}
          />
          {/* Beside the filters it clears, and only while one is on. */}
          {filtered ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clear}
              className="text-muted-foreground hover:text-foreground h-9 gap-1.5"
            >
              <XIcon className="size-4" />
              Clear filters
            </Button>
          ) : null}
        </div>
      )}

      <DataTable
        caption="Service orders"
        title={`${filtered ? 'Matching orders' : 'All orders'} (${shown.length})`}
        // Only with rows: an empty table shows its empty state, and no line under it.
        footer={
          shown.length === 0 ? undefined : (
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={shown.length}
              onPageChange={setPage}
              ariaLabel="Pages of the service orders"
            />
          )
        }
        columns={columns}
        data={paged.rows}
        getRowId={(order) => order.id}
        empty={
          orders.length > 0 ? (
            <FilteredEmpty
              message={
                search.q === undefined
                  ? 'No service orders in this status'
                  : `No service orders matching “${search.q}”`
              }
              onClear={clear}
            />
          ) : (
            <EmptyState
              icon={<PackageIcon />}
              title="No service orders yet"
              description="An exhibitor orders services in the portal after you approve the application. You can also take an order for an exhibitor."
            />
          )
        }
      />
    </div>
  )
}

function OrderLink({ order, children }: { order: ServiceOrder; children: ReactNode }) {
  return (
    <Link
      to="/exhibition/service-orders/$serviceOrderId"
      params={{ serviceOrderId: order.id }}
      className="hover:underline"
    >
      {children}
    </Link>
  )
}
